import { Logger as MwaLogger } from "@lululemon/mwa-logger";
import { isEnvVariablePresent } from "@lululemon/internal-pdp-utils/src/util";
import { startLogger } from "./logger";

// Importing this file has a side effect of instantiating the logger
const mwaLogger = new MwaLogger({ app: "pdp-app", owner: "pdp" });

export const addAdditionalProperties = (scope, props = {}) => {
  props.productId && scope?.setTag("productId", props.productId);
  props.itemDeliveryType &&
    scope?.setTag("itemDeliveryType", props.itemDeliveryType);
  props.isMainPurchaseFlow &&
    scope?.setTag("isMainPurchaseFlow", props.isMainPurchaseFlow);
  scope?.setTag("serverSide", typeof window === "undefined");
};

/**
 * Utility for wrapping PDP errors to specifically route to Sentry under pdp-app.
 * Sentry does not like objects, so objects must be converted to strings.
 *
 * @param {Error|string} errorOrString error (preferred, as the stack will log at that point) or string
 * @param {object} props catch-all for functionName, featureName, cause, tags, etc...
 * @param {function} scopeFn scope allows tags and other metadata
 * @param {string} type mwa-logger supported log levels and "debug"
 * @returns {object} object with functions
 */
const pdpCustomLogger = ({
  errorOrString,
  props,
  scopeFn = null,
  type = "debug"
}) => {
  const {
    fingerprint = ["{{ default }}"],
    featureName = "",
    functionName = "",
    req = null,
    tags,
    ...additionalProps
  } = props ?? {};
  const { cause, code, error } = additionalProps;
  const tagsDefault = {
    componentName: tags?.componentName || "ProductDetailPageContainer",
    featureName: tags?.featureName || featureName || "PDP",
    functionName: tags?.functionName || functionName || "Unknown"
  };
  // avoid PPI and bloat
  const stripReq = {
    originalUrl: req?.originalUrl,
    query: req?.query
  };
  const logInfo = {
    req: stripReq,
    tags: tagsDefault,
    fingerprint: fingerprint
  };

  let newError = errorOrString;
  let newCause = "";
  let newCauseCode = ""; // graphql, RA
  if (
    typeof errorOrString === "string" &&
    type !== "info" &&
    type !== "debug"
  ) {
    newError = new Error(errorOrString || "Unknown error");
    newCause = cause?.message || errorOrString;
    newCauseCode = cause?.code ?? error?.code ?? code;
  } else {
    // Sentry does not want the error object in cause.
    newCause = cause?.message ?? error?.message ?? errorOrString?.message;
    newCauseCode = errorOrString?.code ?? cause?.code;
  }

  // (scope) => {} scope allows tags and other metadata)
  let newScopeFn = scopeFn;
  if (!scopeFn) {
    newScopeFn = (scope) => {
      addAdditionalProperties(scope, additionalProps);
      scope?.setTag("cause", newCause);
      newCauseCode && scope?.setTag("causeCode", newCauseCode);
    };
  }
  if (
    isEnvVariablePresent("NODE_ENV") &&
    process.env.NODE_ENV === "development"
  ) {
    console.log(newError); // our console logs are being hidden
  }
  // eslint-disable-next-line security/detect-object-injection
  return mwaLogger?.[type]?.(newError, logInfo, newScopeFn);
};

export const startPdpLogger = () => {
  startLogger(pdpCustomLogger);
};
